import React, {Fragment, PureComponent} from 'react';
import I18n from 'app/i18n';
import {getLinkPath} from 'app/utils/link_helper';
import logoFramedSvg from 'images/svg_icons/logo-framed.svg';
import appRuPng from 'images/app/app-ru.png';
import appEnPng from 'images/app/app-en.png';
import appIosSvg from 'images/svg_icons/app-apple.svg';
import appAndroidSvg from 'images/svg_icons/app-android.svg';
import {Link} from 'react-router-dom';
import {scrollToTop} from 'app/utils';
import logEvent from 'app/amplitude/log_event';
import {toastSuccess} from 'app/components/toasts/liteToast';
import {clearPwaInstallPrompt, getPwaInstallPrompt, setPwaInstallPrompt} from 'app/utils/pwaInstallPrompt';
import {PLATFORM_IMAGES, APK_IMAGES, TELEGRAM_IMAGES} from 'app/components/pages/apps/apps_images';

const IOS = 'ios';
const ANDROID = 'android';
const TELEGRAM = 'telegram';

export default class AppsPage extends PureComponent {
    rootElement = React.createRef();

    manualInstallRef = React.createRef();

    componentDidMount() {
        this.handleBeforeInstallPrompt = e => {
            e.preventDefault();
            setPwaInstallPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    }

    get platform() {
        return this.isAppleDevice() ? IOS : ANDROID;
    }

    get locale() {
        return I18n.locale === 'ru' ? 'ru' : 'en';
    }

    handleDownloadApk = () => logEvent('MOBILE_WRAPPER_DOWNLOADED');

    isIos() {
        return (/iphone|ipad|ipod/u).test(window.navigator.userAgent.toLowerCase());
    }

    isAppleDevice() {
        return (/macintosh|macintel|iphone|ipad|ipod/u).test(window.navigator.userAgent.toLowerCase());
    }

    installApp = () => {
        const isStandaloneIos = 'standalone' in window.navigator && window.navigator.standalone;
        const isStandaloneAndroid = window.matchMedia('(display-mode: standalone)').matches;

        if (isStandaloneIos || isStandaloneAndroid) {
            toastSuccess(I18n.t('apps_page.pwa_already_installed'));
            return;
        }

        const pwaPrompt = getPwaInstallPrompt();

        if (pwaPrompt) {
            pwaPrompt.prompt();
            pwaPrompt.userChoice.then(() => {
                clearPwaInstallPrompt();
            });
            return;
        }

        if (this.manualInstallRef.current) {
            const elementTop = this.manualInstallRef.current.getBoundingClientRect().top + window.scrollY;
            const headerOffset = 80;

            window.scrollTo({behavior: 'smooth', top: elementTop - headerOffset});
        }
    };

    renderPlatformItems() {
        return PLATFORM_IMAGES[this.platform][this.locale]
            .map((imageSrc, index) => <div className="app-info__item" key={this.platform + this.locale + index}>
                <div className="app-info__item-num">{index + 1}</div>
                <p className="app-info__item-desc">{I18n.t(`apps_page.${this.platform}_${index + 1}`)}</p>
                <img
                    src={imageSrc}
                    className="app-info__item-img"
                    alt={I18n.t('apps_page.manual_install_step_alt', {platform: this.platform, step: index + 1})}
                />
            </div>);
    }

    renderTelegramItems() {
        return TELEGRAM_IMAGES[this.locale]
            .map((imageSrc, index) => <div className="app-info__item" key={`telegram-${this.locale}-${index}`}>
                <div className="app-info__item-num">{index + 1}</div>
                <p className="app-info__item-desc">{I18n.t(`apps_page.telegram.step_${index + 1}`)}</p>
                <img
                    src={imageSrc}
                    className="app-info__item-img"
                    alt={I18n.t('apps_page.telegram.to_link_bot_alt', {step: index + 1})}
                />
            </div>);
    }

    renderPlatformIcon = (platform = 'android') => {
        const icons = {
            android: appAndroidSvg,
            ios: appIosSvg
        };

        return (
            <img
                src={icons[platform]}
                alt={`${platform} icon`}
                width="30px"
                height="30px"
            />
        );
    };

    render() {
        const PWA = 'pwa';
        const platformClassName = `app-info__platform app-info__platform--${this.platform}`;

        return <Fragment>
            <div ref={this.rootElement} className="app-info">
                <div className="app-info__top">
                    <div className="app-info__top-header app-info-gradient">
                        <Link to={getLinkPath('/')} className="app-info__top-logo">
                            <img src={logoFramedSvg} alt="EGB.com"/>
                        </Link>
                        <div className="app-info__top-title">
                            <p className="app-info__top-title-egb">EGB</p>
                            <h1 className="app-info__top-text app-info__top-title-desc">
                                {I18n.t('mobile_web-app_with_whole_site_functional')}
                            </h1>
                        </div>
                    </div>
                    <div className="app-info__top-footer app-info-gradient">
                        <div className="app-info__top-image">
                            <img src={I18n.locale === 'ru' ? appRuPng : appEnPng} alt="" />
                        </div>
                        <div className="app-info__top-install">
                            <p className="app-info__top-text">{I18n.t('apps_page.install_app')}:</p>
                            <div className="app-info__btns">
                                <button
                                    className="app-info__btn"
                                    onClick={this.installApp}
                                >
                                    {this.renderPlatformIcon(IOS)}
                                    {this.renderPlatformIcon(ANDROID)}
                                    <span>{PWA}</span>
                                </button>
                                {!this.isAppleDevice() && <Link
                                    to={getLinkPath('/apps/android')}
                                    className="app-info__btn app-info__btn--simple"
                                    onClick={scrollToTop}
                                >
                                    {this.renderPlatformIcon(ANDROID)}
                                    <span>{ANDROID}</span>
                                </Link>}
                                <a
                                    href="https://t.me/egb_live_bot"
                                    rel="noreferrer"
                                    target="_blank"
                                    className="app-info__btn app-info__btn--simple"
                                >
                                    <div className="app-info__telegram-icon"/>
                                    <span>{TELEGRAM}</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="app-info__content">
                    <p className="app-info__text">{I18n.t('apps_page.choose_a_convenient_option')}</p>
                    <div className="app-info__platform app-info__platform--pwa">
                        <div className="app-info__platform-title">
                            {this.renderPlatformIcon(IOS)}
                            {this.renderPlatformIcon(ANDROID)}
                            <span>{PWA}</span>
                        </div>
                        <p className="app-info__text">{I18n.t('apps_page.pwa')}</p>
                    </div>
                    <p className="app-info__text">{I18n.t('apps_page.if_fails_use_manual_install')}</p>
                    <div ref={this.manualInstallRef} className={`app-info__platform ${platformClassName}`}>
                        <div className="app-info__platform-title">
                            {this.renderPlatformIcon(this.platform)}
                            <span>{this.platform}</span>
                        </div>
                        <div className="app-info__box">
                            {this.renderPlatformItems()}
                        </div>
                    </div>
                    <p className="app-info__text">{I18n.t('apps_page.installation_less_than_a_minute')}</p>
                    {!this.isAppleDevice() && <div className="app-info__platform">
                        <div className="app-info__platform-title">
                            {this.renderPlatformIcon(ANDROID)}
                            <span>Android - APK</span>
                        </div>
                        <div className="app-info__box-apk-text">
                            <p>
                                {I18n.t('apps_page.apk.step_1_1')}
                                «<a
                                    href={getLinkPath('/apps/android/download/')}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                    onClick={this.handleDownloadApk}
                                >
                                    {I18n.t('download_app')}
                                </a>»
                                {I18n.t('apps_page.apk.step_1_2')}
                            </p>
                            <p>{I18n.t('apps_page.apk.step_2')}</p>
                            <p>{I18n.t('apps_page.apk.step_3')}</p>
                            <p>{I18n.t('apps_page.apk.step_4')}</p>
                            <p>{I18n.t('apps_page.apk.step_5')}</p>
                        </div>
                        <div className="app-info__box-apk">
                            {APK_IMAGES.map((imageSrc, index) => <img
                                src={imageSrc}
                                key={`apk-image-${index}`}
                                alt={I18n.t('apps_page.manual_install_step_alt', {platform: `${ANDROID} - APK`, step: index + 1})}
                            />)}
                        </div>
                    </div>}
                    <div className="app-info__platform app-info__platform--telegram">
                        <div className="app-info__platform-title">
                            <div className="app-info__telegram-icon"/>
                            <span>{TELEGRAM}</span>
                        </div>
                        <p className="app-info__text" dangerouslySetInnerHTML={{__html: I18n.t('apps_page.telegram.description')}} />
                        <div className="app-info__box">
                            {this.renderTelegramItems()}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>;
    }
}
